// smartToolsTaxonomy — the ONE place the Smart Tools Browser (and the slimmed
// Smart drawer) reads its instructional-domain vocabulary from. Pure data +
// pure functions, no React, imports nothing — so it parses standalone and the
// translations dev has a single file to localize.
//
// A recipe declares an OPTIONAL `domain` (posterTools/types.ts ToolRecipe). The
// domain is the recipe's HONEST K-12 home and MAY differ from its legacy
// `category` (handwriting-lines is category:'math' but domain:'ela'; timeline
// is category:'math' but domain:'social'). Recipes that predate the taxonomy
// (or forget the field) fall back through CATEGORY_TO_DOMAIN, so the browser is
// never left with an un-homed tool.
//
// i18n: DOMAIN_LABELS is the translation surface — swap the right-hand strings
// (or repoint domainLabel() at a lookup) and every rail heading follows. The
// browser future-proofs by rendering WHATEVER domains exist in the data, in
// DOMAIN_ORDER (known ids first, unknown ids appended alphabetically), so a new
// recipe carrying `domain:'music'` needs no browser change.

// The 12 domains the current catalog uses (owner list, 2026-07-23) followed by
// the V2 gap-review ids (music/pe/cs/library) — labels kept ready so a future
// recipe in one of those domains renders a real heading, not a raw id. Grows
// toward the strategy doc's ~16-domain / 220-tool catalog.
export const DOMAIN_LABELS = {
  math: 'Math',
  ela: 'English / Language Arts',
  science: 'Science',
  social: 'Social Studies',
  sel: 'Social-Emotional',
  display: 'Sensory & Display',
  sped: 'SPED & Inclusion',
  cte: 'Career & Tech Ed',
  languages: 'World Languages',
  arts: 'Arts & Music',
  observances: 'Observances',
  ops: 'Classroom Ops',
  // --- V2 §E gap-review domains (no recipes yet; labels ready for translation)
  music: 'Music',
  pe: 'PE & Health',
  cs: 'Computer Science',
  library: 'Library & Media',
  // defensive fallback (a recipe with an unmapped category)
  other: 'Other',
};

// Rail order — the 12 named domains lead, gap-review ids follow. Any domain id
// present in the data but absent here is appended (alphabetically) by
// buildDomainRail, so the rail can never drop a real tool.
export const DOMAIN_ORDER = [
  'math', 'ela', 'science', 'social', 'sel', 'display',
  'sped', 'cte', 'languages', 'arts', 'observances', 'ops',
  'music', 'pe', 'cs', 'library', 'other',
];

// Legacy category → domain, the fallback when a recipe omits `domain`. Every
// current recipe declares an explicit domain, so this only catches future
// recipes that forget the field.
export const CATEGORY_TO_DOMAIN = {
  chart: 'math',
  math: 'math',
  science: 'science',
  practice: 'ela',
  decor: 'display',
  callouts: 'display',
  text: 'display',
};

// Categories that DON'T belong in the Smart browser/drawer: 'text' (art-text)
// lives under the Text panel's "Text Effects" by owner directive — the browser
// mirrors that exclusion. Flip this set (and nothing else) to surface them.
export const BROWSER_EXCLUDED_CATEGORIES = new Set(['text']);

// Pseudo-domain rail ids (never a real recipe domain).
export const FEATURED_DOMAIN = '__featured';
export const ALL_DOMAIN = '__all';

/** Human label for a domain id (i18n seam). Unknown → the id itself. */
export function domainLabel(id) {
  return DOMAIN_LABELS[id] || id;
}

/** A recipe's effective domain: explicit `domain`, else category fallback. */
export function recipeDomain(recipe) {
  if (!recipe) return 'other';
  if (typeof recipe.domain === 'string' && recipe.domain) return recipe.domain;
  return CATEGORY_TO_DOMAIN[recipe.category] || 'other';
}

/** True when a recipe should appear in the Smart browser/drawer. */
export function isBrowsable(recipe) {
  return !!recipe && !BROWSER_EXCLUDED_CATEGORIES.has(recipe.category);
}

/** Filter a recipe list down to the browsable set (drops excluded categories). */
export function browsableRecipes(recipes) {
  return (recipes || []).filter(isBrowsable);
}

/**
 * Build the browser's left-rail model from the recipes present: one entry per
 * domain that actually has recipes, ordered by DOMAIN_ORDER (unknown ids
 * appended alphabetically), each with its count. Featured/All are prepended by
 * the browser itself (they're pseudo-domains, counted separately).
 */
export function buildDomainRail(recipes) {
  const counts = new Map();
  for (const r of recipes || []) {
    const d = recipeDomain(r);
    counts.set(d, (counts.get(d) || 0) + 1);
  }
  const present = [...counts.keys()];
  present.sort((a, b) => {
    const ia = DOMAIN_ORDER.indexOf(a);
    const ib = DOMAIN_ORDER.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });
  return present.map((id) => ({ id, label: domainLabel(id), count: counts.get(id) }));
}

/**
 * The drawer's featured set: recipes flagged featured:true, in list order; if
 * fewer than `max`, padded with the first recipe of each not-yet-represented
 * domain (so the grid never looks empty for a lightly-flagged catalog). Capped
 * at `max` (drawer shows 6).
 */
export function featuredRecipes(recipes, max = 6) {
  const browsable = browsableRecipes(recipes);
  const featured = browsable.filter((r) => r.featured);
  if (featured.length >= max) return featured.slice(0, max);
  const picked = new Set(featured.map((r) => r.id));
  const seenDomain = new Set(featured.map((r) => recipeDomain(r)));
  const out = [...featured];
  for (const r of browsable) {
    if (out.length >= max) break;
    if (picked.has(r.id)) continue;
    const d = recipeDomain(r);
    if (seenDomain.has(d)) continue;
    seenDomain.add(d);
    picked.add(r.id);
    out.push(r);
  }
  return out.slice(0, max);
}
